import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { EDITOR_REQUIRED_ANCHORS, EDITOR_TOOL_KEYS } from '@editor/config/constants'
import { EDITOR_FRAME_COLORS } from '@editor/config/styles'
import { EditorContext } from '@editor/contexts/Editor'
import { Cuboid, CuboidAnchor, CuboidFrameProps } from '@editor/interfaces/interfaces'
import { getCuboidFromPoints, pointsToVector3s } from '@editor/services/Points'
import { millimeterToMeter } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setIsDragging } from '@editor/store/tool'
import { TransformControls } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { isTablet } from 'react-device-detect'
import { useSelector } from 'react-redux'
import { BoxGeometry, EdgesGeometry, Group, Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { TransformControls as TransformControlsImpl } from 'three-stdlib'

const MIN_DIAMETER_SCALE = 10

const CuboidFrame = ({ cuboidAnchor, baseDiameter, maxSize, onChange, onChanged }: CuboidFrameProps) => {
  const { selectedTool } = useContext(EditorContext)
  const lineRef = useRef<Group>(null)
  const groupRef = useRef<Group>(null)
  const transformRef = useRef<TransformControlsImpl>(null)

  const dispatch = useAppDispatch()
  const selectedTransform = useSelector((state: RootState) => state.toolUtil.core.selectedTransform)

  const [cuboidInfo, setCuboidInfo] = useState<{
    width: number
    depth: number
    height: number
    position: Vector3
    quaternion: Quaternion
    edgesGeometry: EdgesGeometry<BoxGeometry>
    rotationMatrix: Matrix4
    axisPoints: { x: Vector3[]; y: Vector3[]; z: Vector3[] }
  }>()
  const prevCuboidAnchorRef = useRef<CuboidAnchor>(undefined)

  const convertedDiameter = millimeterToMeter(baseDiameter || MIN_DIAMETER_SCALE)

  /**
   * Generate cuboid data from anchor points and diameter, with limit check, return default values if not ready
   */
  const generateCuboid = useCallback((): Cuboid => {
    if (!lineRef.current || !transformRef.current || !cuboidInfo) {
      return {
        center: [0, 0, 0],
        rotation: new Matrix3().setFromMatrix4(new Matrix4()).toArray(),
        extent: [0, 0, 0],
      }
    }

    const minScaleX = convertedDiameter / (cuboidInfo?.width || 1)
    const minScaleY = convertedDiameter / (cuboidInfo?.height || 1)
    const minScaleZ = convertedDiameter / (cuboidInfo?.depth || 1)
    const maxScaleX = maxSize / (cuboidInfo?.width || 1)
    const maxScaleY = maxSize / (cuboidInfo?.height || 1)
    const maxScaleZ = maxSize / (cuboidInfo?.depth || 1)

    const { scale } = lineRef.current
    let { width, depth, height } = cuboidInfo
    width *= scale.x
    height *= scale.y
    depth *= scale.z

    let needReset = false
    if (minScaleX > scale.x) {
      needReset = true
      lineRef.current.scale.x = minScaleX
      width = convertedDiameter
    }
    if (minScaleY > scale.y) {
      needReset = true
      lineRef.current.scale.y = minScaleY
      height = convertedDiameter
    }
    if (minScaleZ > scale.z) {
      needReset = true
      lineRef.current.scale.z = minScaleZ
      depth = convertedDiameter
    }
    if (maxScaleX < scale.x) {
      needReset = true
      lineRef.current.scale.x = maxScaleX
      width = maxSize
    }
    if (maxScaleY < scale.y) {
      needReset = true
      lineRef.current.scale.y = maxScaleY
      height = maxSize
    }
    if (maxScaleZ < scale.z) {
      needReset = true
      lineRef.current.scale.z = maxScaleZ
      depth = maxSize
    }

    if (needReset) {
      transformRef.current.detach()
      transformRef.current.attach(lineRef.current)
    }

    return {
      center: lineRef.current.getWorldPosition(new Vector3()).toArray(),
      rotation: new Matrix3().setFromMatrix4(new Matrix4().extractRotation(lineRef.current.matrixWorld)).toArray(),
      extent: [width, height, depth],
    }
  }, [convertedDiameter, maxSize, cuboidInfo])

  /**
   * Update cuboid frame when anchor points or diameter changed
   */
  const updateCuboid = useCallback(() => {
    onChange?.(generateCuboid())
  }, [onChange, generateCuboid])

  useEffect(() => {
    if (cuboidAnchor?.points.length !== EDITOR_REQUIRED_ANCHORS.cuboid) {
      prevCuboidAnchorRef.current = undefined
      return
    }

    // if cuboid has been initialized, no need to recalculate
    if (prevCuboidAnchorRef.current) {
      return
    }
    prevCuboidAnchorRef.current = cuboidAnchor

    const { width, depth, height, position, quaternion, edgesGeometry, rotationMatrix, axisPoints } =
      getCuboidFromPoints(pointsToVector3s(cuboidAnchor.points), convertedDiameter)

    onChange?.({
      center: position.toArray(),
      rotation: new Matrix3().setFromMatrix4(new Matrix4().extractRotation(rotationMatrix)).toArray(),
      extent: [width, height, depth],
    })

    setCuboidInfo({
      width,
      depth,
      height,
      position,
      quaternion,
      edgesGeometry,
      rotationMatrix,
      axisPoints,
    })
  }, [convertedDiameter, cuboidAnchor, onChange])

  useEffect(() => {
    // if cuboid has not been initialized, no need to recalculate
    if (!cuboidInfo) {
      return
    }

    updateCuboid()
  }, [convertedDiameter, cuboidInfo, updateCuboid])

  /**
   * Reset cuboid frame when anchor points destroyed
   */
  useEffect(() => {
    if (!cuboidAnchor) {
      setCuboidInfo(undefined)
    }
  }, [cuboidAnchor])

  useFrame(() => {
    if (lineRef.current && transformRef.current && cuboidAnchor) {
      transformRef.current.visible = true
      lineRef.current.visible = true
      transformRef.current.attach(lineRef.current)
    }

    // reset the values when cuboid destroyed
    if (lineRef.current && transformRef.current && !cuboidAnchor) {
      transformRef.current.visible = false
      transformRef.current.position.set(0, 0, 0)
      transformRef.current.setRotationFromMatrix(new Matrix4())
      transformRef.current.clear()
      lineRef.current.visible = false
      lineRef.current.position.set(0, 0, 0)
      lineRef.current.scale.set(1, 1, 1)
    }
  })

  if (cuboidAnchor?.points.length !== EDITOR_REQUIRED_ANCHORS.cuboid || !cuboidInfo) {
    return null
  }

  return (
    <>
      {/* ref must be any */}
      <TransformControls
        ref={transformRef}
        size={isTablet ? 1 : 0.5}
        onObjectChange={updateCuboid}
        mode={selectedTransform}
        onMouseDown={() => {
          dispatch(setIsDragging(true))
        }}
        onMouseUp={() => {
          onChanged?.(generateCuboid())
        }}
        space="local"
      />
      <group ref={groupRef} position={cuboidInfo.position} quaternion={cuboidInfo.quaternion}>
        <group ref={lineRef}>
          <lineSegments geometry={cuboidInfo.edgesGeometry}>
            <lineBasicMaterial color={EDITOR_FRAME_COLORS[EDITOR_TOOL_KEYS[selectedTool]]} />
          </lineSegments>
        </group>
      </group>
    </>
  )
}
export default CuboidFrame
